/**
 * The variable catalog — the single source of truth for **both** halves of the feature.
 *
 * A template author picks `{firstName}` from a chip list; a rule author picks "קטגוריה" from a
 * dropdown. Those are the same catalog read two ways, and that is the point: the requirement was
 * "every field on an order should be usable as a variable and as a condition", so a field that can
 * be displayed and not conditioned on (or the reverse) is a bug in this file, not a design.
 *
 * ## Why the tokens are in English
 *
 * The labels are Hebrew and the tokens are not. A token is typed into a rich-text editor, survives
 * copy-paste through a mail provider, and appears in an HTML attribute now and then; `{firstName}`
 * does all of that without an encoding question, and it matches the two tokens the mailing dialog
 * already offers (`{name}`, `{amount}`). Nobody types a token by hand — the chip inserts it.
 *
 * ## Why resolvers live in `libs/types`
 *
 * They are pure functions over `OrderMailingContext`, so they are testable with `nx test types`
 * (where the rest of this module's logic is tested), and the preview screen renders with the exact
 * code the sender uses rather than an approximation of it.
 */

import {
  OrderMailingContext,
  formatTemplateAddress,
  formatTemplateDate,
  formatTemplateDateTime,
  formatTemplateMoney,
  uniqueNumbers,
  uniqueStrings
} from './orderMailing.context';
import {
  ORDER_FULFILLMENT_STATUS_LABELS,
  ORDER_PAYMENT_STATUS_LABELS,
  ORDER_STATUS_LABELS,
  type OrderFulfillmentStatus,
  type OrderPaymentStatus,
  type OrderStatus,
  orderCustomerName
} from './productHub.orders';

/* ------------------------------------------------------------------ */
/* Groups                                                              */
/* ------------------------------------------------------------------ */

export const VARIABLE_GROUPS = [
  'customer',
  'order',
  'items',
  'classification',
  'shipping',
  'pickup',
  'warehouse',
  'store'
] as const;
export type VariableGroup = (typeof VARIABLE_GROUPS)[number];

export const VARIABLE_GROUP_LABELS: Record<VariableGroup, string> = {
  customer: 'מזמין',
  order: 'הזמנה',
  items: 'פריטים',
  classification: 'סיווג',
  shipping: 'משלוח',
  pickup: 'איסוף',
  warehouse: 'מחסן',
  store: 'חנות'
};

/* ------------------------------------------------------------------ */
/* Conditions                                                          */
/* ------------------------------------------------------------------ */

export type MailingConditionType = 'select' | 'number' | 'text';

/**
 * Where the rule builder gets the values to choose between.
 *
 * A name rather than an inline list, because these are rows (categories, warehouses, stores) or
 * enums that already exist elsewhere. The frontend maps the name to the query it already has.
 */
export type ConditionOptionSource =
  | 'orderStatus'
  | 'paymentStatus'
  | 'fulfillmentStatus'
  | 'category'
  | 'tag'
  | 'brand'
  | 'product'
  | 'shippingMethod'
  | 'shippingProvider'
  | 'warehouse'
  | 'store';

export interface MailingConditionValueOptions {
  /** Category only — see `supportsDescendants`. */
  includeDescendants?: boolean;
}

export interface OrderMailingVariable {
  /** Written `{token}` in a template. Also the condition's `field`. */
  token: string;
  label: string;
  group: VariableGroup;
  /** Shown under the chip; use it for the thing that is not obvious from the label. */
  hint?: string;
  /** Plain text. This is what SMS gets, and what email gets unless `resolveHtml` exists. */
  resolve: (ctx: OrderMailingContext) => string;
  /** The richer form, for email only. */
  resolveHtml?: (ctx: OrderMailingContext) => string;
  conditionType?: MailingConditionType;
  optionSource?: ConditionOptionSource;
  /** Overrides `label` in the rule builder, where a different word reads better. */
  conditionLabel?: string;
  /** Whether "כולל תתי-קטגוריות" applies. Only the category field sets it. */
  supportsDescendants?: boolean;
  /**
   * The order's own values for this field, to compare against the ones the rule names.
   *
   * An array even for a single-valued field, because an item-level field genuinely has several
   * values (an order holds three products in two categories) and the comparison is the same
   * either way: **does any of the order's values appear in the rule's list**. That is the ∃
   * semantics the feature was specified with — an order containing a book and a pamphlet enters
   * both the book rule and the pamphlet rule and receives both messages.
   */
  conditionValues?: (ctx: OrderMailingContext, options: MailingConditionValueOptions) => (string | number)[];
}

/* ------------------------------------------------------------------ */
/* Item rendering                                                      */
/* ------------------------------------------------------------------ */

const itemLine = (item: OrderMailingContext['items'][number], currency: string): string => {
  const name = item.variantLabel ? `${item.productName} (${item.variantLabel})` : item.productName;
  return `${name} × ${item.quantity} — ${formatTemplateMoney(item.lineTotal, currency)}`;
};

/**
 * The lines as an HTML table, with **inline** styles.
 *
 * Not a stylesheet: mail clients strip `<style>` blocks often enough that a table which depends on
 * one arrives as unformatted text. `dir="rtl"` is on the table itself for the same reason.
 */
const itemsTableHtml = (ctx: OrderMailingContext): string => {
  const cell = 'padding:6px 10px;border-bottom:1px solid #e0e0e0;text-align:right;';
  const head = `${cell}font-weight:bold;background:#f5f5f5;`;
  const rows = ctx.items
    .map(item => {
      const name = item.variantLabel ? `${item.productName} (${item.variantLabel})` : item.productName;
      return (
        `<tr><td style="${cell}">${escapeHtml(name)}</td>` +
        `<td style="${cell}">${escapeHtml(item.sku ?? '')}</td>` +
        `<td style="${cell}">${item.quantity}</td>` +
        `<td style="${cell}">${escapeHtml(formatTemplateMoney(item.lineTotal, ctx.order.currency))}</td></tr>`
      );
    })
    .join('');

  return (
    `<table dir="rtl" style="border-collapse:collapse;width:100%;font-family:inherit;">` +
    `<thead><tr><th style="${head}">מוצר</th><th style="${head}">מק״ט</th>` +
    `<th style="${head}">כמות</th><th style="${head}">סה״כ</th></tr></thead>` +
    `<tbody>${rows}</tbody></table>`
  );
};

/** Only what a product name or SKU can realistically contain. Templates themselves are trusted. */
const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/* ------------------------------------------------------------------ */
/* The catalog                                                         */
/* ------------------------------------------------------------------ */

const itemsOf = (ctx: OrderMailingContext) => ctx.items;

export const ORDER_MAILING_VARIABLES: OrderMailingVariable[] = [
  /* ── מזמין ───────────────────────────────────────────────────────── */
  { token: 'firstName', label: 'שם פרטי', group: 'customer', resolve: ctx => ctx.customer.firstName },
  { token: 'lastName', label: 'שם משפחה', group: 'customer', resolve: ctx => ctx.customer.lastName },
  {
    token: 'fullName',
    label: 'שם מלא',
    group: 'customer',
    resolve: ctx => orderCustomerName(ctx.customer.firstName, ctx.customer.lastName)
  },
  { token: 'email', label: 'אימייל', group: 'customer', resolve: ctx => ctx.customer.email },
  { token: 'phone', label: 'טלפון', group: 'customer', resolve: ctx => ctx.customer.phone },

  /* ── הזמנה ───────────────────────────────────────────────────────── */
  { token: 'orderNumber', label: 'מספר הזמנה', group: 'order', resolve: ctx => ctx.order.externalNumber },
  { token: 'orderDate', label: 'תאריך הזמנה', group: 'order', resolve: ctx => formatTemplateDate(ctx.order.placedAt) },
  {
    token: 'orderDateTime',
    label: 'תאריך ושעת הזמנה',
    group: 'order',
    resolve: ctx => formatTemplateDateTime(ctx.order.placedAt)
  },
  {
    token: 'orderStatus',
    label: 'סטטוס הזמנה',
    group: 'order',
    resolve: ctx => ORDER_STATUS_LABELS[ctx.order.status as OrderStatus] ?? ctx.order.status,
    conditionType: 'select',
    optionSource: 'orderStatus',
    conditionValues: ctx => [ctx.order.status]
  },
  {
    token: 'paymentStatus',
    label: 'סטטוס תשלום',
    group: 'order',
    resolve: ctx => ORDER_PAYMENT_STATUS_LABELS[ctx.order.paymentStatus as OrderPaymentStatus] ?? ctx.order.paymentStatus,
    conditionType: 'select',
    optionSource: 'paymentStatus',
    conditionValues: ctx => [ctx.order.paymentStatus]
  },
  {
    token: 'fulfillmentStatus',
    label: 'סטטוס אספקה',
    group: 'order',
    resolve: ctx =>
      ORDER_FULFILLMENT_STATUS_LABELS[ctx.order.fulfillmentStatus as OrderFulfillmentStatus] ??
      ctx.order.fulfillmentStatus,
    conditionType: 'select',
    optionSource: 'fulfillmentStatus',
    conditionValues: ctx => [ctx.order.fulfillmentStatus]
  },
  {
    token: 'grandTotal',
    label: 'סה״כ לתשלום',
    group: 'order',
    resolve: ctx => formatTemplateMoney(ctx.order.grandTotal, ctx.order.currency),
    conditionType: 'number',
    conditionLabel: 'סה״כ לתשלום (₪)',
    conditionValues: ctx => [ctx.order.grandTotal]
  },
  {
    token: 'itemsSubtotal',
    label: 'סכום הפריטים',
    group: 'order',
    resolve: ctx => formatTemplateMoney(ctx.order.itemsSubtotal, ctx.order.currency)
  },
  {
    token: 'shippingTotal',
    label: 'דמי משלוח',
    group: 'order',
    resolve: ctx => formatTemplateMoney(ctx.order.shippingTotal, ctx.order.currency)
  },
  {
    token: 'discountTotal',
    label: 'הנחה',
    group: 'order',
    resolve: ctx => formatTemplateMoney(ctx.order.discountTotal, ctx.order.currency)
  },
  {
    token: 'taxTotal',
    label: 'מע״מ',
    group: 'order',
    resolve: ctx => formatTemplateMoney(ctx.order.taxTotal, ctx.order.currency)
  },
  { token: 'currency', label: 'מטבע', group: 'order', resolve: ctx => ctx.order.currency },
  {
    token: 'unitsCount',
    label: 'מספר יחידות',
    group: 'order',
    resolve: ctx => String(ctx.order.unitsCount),
    conditionType: 'number',
    conditionValues: ctx => [ctx.order.unitsCount]
  },
  {
    token: 'linesCount',
    label: 'מספר שורות',
    group: 'order',
    resolve: ctx => String(ctx.order.linesCount),
    conditionType: 'number',
    conditionValues: ctx => [ctx.order.linesCount]
  },
  { token: 'customerNote', label: 'הערת הלקוח', group: 'order', resolve: ctx => ctx.order.customerNote ?? '' },

  /* ── פריטים ──────────────────────────────────────────────────────── */
  {
    token: 'itemsList',
    label: 'רשימת פריטים',
    group: 'items',
    hint: 'שורה לכל פריט — מתאים גם ל-SMS',
    resolve: ctx => ctx.items.map(item => itemLine(item, ctx.order.currency)).join('\n')
  },
  {
    token: 'itemsTable',
    label: 'טבלת פריטים',
    group: 'items',
    hint: 'טבלה מעוצבת באימייל; ב-SMS מוצגת כרשימה',
    resolve: ctx => ctx.items.map(item => itemLine(item, ctx.order.currency)).join('\n'),
    resolveHtml: itemsTableHtml
  },
  {
    token: 'firstItemName',
    label: 'שם הפריט הראשון',
    group: 'items',
    resolve: ctx => ctx.items[0]?.productName ?? ''
  },
  {
    token: 'productNames',
    label: 'שמות המוצרים',
    group: 'items',
    resolve: ctx => uniqueStrings(itemsOf(ctx).map(item => item.productName)).join(', ')
  },
  {
    token: 'skus',
    label: 'מק״טים',
    group: 'items',
    resolve: ctx => uniqueStrings(itemsOf(ctx).map(item => item.sku)).join(', '),
    conditionType: 'text',
    conditionLabel: 'מק״ט בהזמנה',
    conditionValues: ctx => uniqueStrings(itemsOf(ctx).map(item => item.sku))
  },
  {
    token: 'products',
    label: 'מוצרים בהזמנה',
    group: 'items',
    hint: 'לשימוש כתנאי — בחירת מוצר מסוים',
    resolve: ctx => uniqueStrings(itemsOf(ctx).map(item => item.productName)).join(', '),
    conditionType: 'select',
    optionSource: 'product',
    conditionLabel: 'מוצר',
    conditionValues: ctx =>
      uniqueNumbers(itemsOf(ctx).reduce<number[]>((acc, item) => (item.productId ? acc.concat(item.productId) : acc), []))
  },

  /* ── סיווג ───────────────────────────────────────────────────────── */
  {
    token: 'categories',
    label: 'קטגוריות',
    group: 'classification',
    conditionLabel: 'קטגוריה / תת-קטגוריה',
    hint: 'קטגוריה ותת-קטגוריה הן אותו עץ — בחירת קטגוריית אב תופסת גם את מה שמתחתיה',
    resolve: ctx => uniqueStrings(itemsOf(ctx).map(item => item.categoryName)).join(', '),
    conditionType: 'select',
    optionSource: 'category',
    supportsDescendants: true,
    // With descendants (the default) the whole path is offered, so naming a parent matches a child.
    // Without, only the category the item actually sits in.
    conditionValues: (ctx, options) =>
      uniqueNumbers(
        itemsOf(ctx).reduce<number[]>(
          (acc, item) =>
            acc.concat(options.includeDescendants === false ? (item.categoryId ? [item.categoryId] : []) : item.categoryPathIds),
          []
        )
      )
  },
  {
    token: 'tags',
    label: 'תגיות מוצר',
    group: 'classification',
    resolve: ctx => uniqueStrings(itemsOf(ctx).reduce<string[]>((acc, item) => acc.concat(item.tagNames), [])).join(', '),
    conditionType: 'select',
    optionSource: 'tag',
    conditionLabel: 'תגית מוצר',
    conditionValues: ctx => uniqueNumbers(itemsOf(ctx).reduce<number[]>((acc, item) => acc.concat(item.tagIds), []))
  },
  {
    token: 'brands',
    label: 'מותגים',
    group: 'classification',
    resolve: ctx => uniqueStrings(itemsOf(ctx).map(item => item.brandName)).join(', '),
    conditionType: 'select',
    optionSource: 'brand',
    conditionLabel: 'מותג',
    conditionValues: ctx =>
      uniqueNumbers(itemsOf(ctx).reduce<number[]>((acc, item) => (item.brandId ? acc.concat(item.brandId) : acc), []))
  },

  /* ── משלוח ───────────────────────────────────────────────────────── */
  {
    token: 'shippingMethod',
    label: 'שיטת אספקה',
    group: 'shipping',
    resolve: ctx => ctx.shipping.methodName ?? '',
    conditionType: 'select',
    optionSource: 'shippingMethod',
    conditionValues: ctx => (ctx.shipping.methodId ? [ctx.shipping.methodId] : [])
  },
  {
    token: 'shippingProvider',
    label: 'חברת שילוח',
    group: 'shipping',
    resolve: ctx => ctx.shipping.providerName ?? '',
    conditionType: 'select',
    optionSource: 'shippingProvider',
    conditionValues: ctx => (ctx.shipping.providerId ? [ctx.shipping.providerId] : [])
  },
  { token: 'trackingNumber', label: 'מספר מעקב', group: 'shipping', resolve: ctx => ctx.shipping.trackingNumber ?? '' },
  { token: 'recipientName', label: 'שם הנמען', group: 'shipping', resolve: ctx => ctx.shipping.recipientName ?? '' },
  {
    token: 'shippingAddress',
    label: 'כתובת למשלוח',
    group: 'shipping',
    resolve: ctx => formatTemplateAddress(ctx.shipping)
  },
  { token: 'shippingCity', label: 'עיר', group: 'shipping', resolve: ctx => ctx.shipping.city ?? '' },
  {
    token: 'shippedAt',
    label: 'תאריך יציאה למשלוח',
    group: 'shipping',
    resolve: ctx => formatTemplateDate(ctx.shipping.shippedAt)
  },
  {
    token: 'deliveredAt',
    label: 'תאריך מסירה',
    group: 'shipping',
    resolve: ctx => formatTemplateDate(ctx.shipping.deliveredAt)
  },

  /* ── איסוף ───────────────────────────────────────────────────────── */
  { token: 'pickupPoint', label: 'נקודת איסוף', group: 'pickup', resolve: ctx => ctx.pickup.pointName ?? '' },
  {
    token: 'readyForPickupAt',
    label: 'מוכן לאיסוף בתאריך',
    group: 'pickup',
    resolve: ctx => formatTemplateDate(ctx.pickup.readyForPickupAt)
  },

  /* ── מחסן ────────────────────────────────────────────────────────── */
  {
    token: 'warehouseName',
    label: 'שם המחסן',
    group: 'warehouse',
    resolve: ctx => ctx.warehouse?.name ?? '',
    conditionType: 'select',
    optionSource: 'warehouse',
    conditionLabel: 'מחסן מטפל',
    conditionValues: ctx => (ctx.warehouse ? [ctx.warehouse.id] : [])
  },
  { token: 'warehouseAddress', label: 'כתובת המחסן', group: 'warehouse', resolve: ctx => ctx.warehouse?.address ?? '' },
  { token: 'warehousePhone', label: 'טלפון המחסן', group: 'warehouse', resolve: ctx => ctx.warehouse?.phone ?? '' },

  /* ── חנות ────────────────────────────────────────────────────────── */
  {
    token: 'storeName',
    label: 'שם החנות',
    group: 'store',
    resolve: ctx => ctx.store.name,
    conditionType: 'select',
    optionSource: 'store',
    conditionLabel: 'חנות',
    conditionValues: ctx => [ctx.store.id]
  }
];

/** Token → variable. Built once; the render path is called per recipient. */
export const VARIABLE_BY_TOKEN: Record<string, OrderMailingVariable> = ORDER_MAILING_VARIABLES.reduce<
  Record<string, OrderMailingVariable>
>((acc, variable) => {
  acc[variable.token] = variable;
  return acc;
}, {});

/** The subset the rule builder offers. Derived, so a new condition needs one entry above. */
export const CONDITION_FIELDS: OrderMailingVariable[] = ORDER_MAILING_VARIABLES.filter(v => !!v.conditionType);

/* ------------------------------------------------------------------ */
/* Rendering                                                           */
/* ------------------------------------------------------------------ */

const TOKEN_PATTERN = /\{([a-zA-Z][a-zA-Z0-9_]*)\}/g;

/** Every `{token}` a body mentions, in order of appearance, without duplicates. */
export const templateTokens = (text: string): string[] => {
  const found: string[] = [];
  let match = TOKEN_PATTERN.exec(text);
  while (match) {
    if (found.indexOf(match[1]) === -1) found.push(match[1]);
    match = TOKEN_PATTERN.exec(text);
  }
  TOKEN_PATTERN.lastIndex = 0;
  return found;
};

export interface RenderResult {
  text: string;
  /** Tokens the catalog does not know. Surfaced by the preview so a typo is visible before a send. */
  unknownTokens: string[];
  /** Known tokens that resolved to nothing — usually a field the order genuinely does not have. */
  emptyTokens: string[];
}

/**
 * Substitute every known token; **remove** the unknown ones.
 *
 * Removing rather than leaving them is the lesser evil for a real send — a customer reading
 * "שלום {frstName}" is worse than reading "שלום ," — and it is safe only because the preview
 * returns `unknownTokens` and the template dialog shows them. The two halves are one decision.
 */
export const renderTemplate = (
  text: string,
  ctx: OrderMailingContext,
  channel: 'sms' | 'email' = 'email'
): RenderResult => {
  const unknownTokens: string[] = [];
  const emptyTokens: string[] = [];

  const rendered = text.replace(TOKEN_PATTERN, (_full, token: string) => {
    const variable = VARIABLE_BY_TOKEN[token];
    if (!variable) {
      if (unknownTokens.indexOf(token) === -1) unknownTokens.push(token);
      return '';
    }
    const value =
      channel === 'email' && variable.resolveHtml ? variable.resolveHtml(ctx) : variable.resolve(ctx);
    if (!value && emptyTokens.indexOf(token) === -1) emptyTokens.push(token);
    return value;
  });

  return { text: rendered, unknownTokens, emptyTokens };
};
